export const BOARD_SIZE = 15;
export const RACK_SIZE = 7;
export const POLL_INTERVAL_MS = 1500;
export const DEBUG = false;
export const REVEAL_DELAY_MS = 5000;
export const LEADERBOARD_TOP_N = 50;

// Penalty & bonus constants
export const PENALTY_INVALID_PLACEMENT = -10;
export const BONUS_FIRST_COMPLETE = 100;
export const BONUS_COMPLETE = 50;
export const TIME_BONUS_PER_SECOND = 1;
export const GRACE_PERIOD_MS = 10000; // 10s buffer for opponent to finish

export const DEFAULT_TIME_LIMIT_MS = 180000;
export const MIN_TIME_LIMIT_MS = 180000; // 3 min
export const MAX_TIME_LIMIT_MS = 300000; // 5 min
export const TIME_LIMIT_STEP_MS = 30000; // 30 sec increments

export const COMPLEXITY_TILE_COUNTS: Record<string, [number, number]> = {
  low: [30, 30],     // 30 tiles each
  medium: [50, 50],  // 50 tiles each
  high: [70, 70],    // 70 tiles each
};

export const COMPLEXITY_MULTIPLIER: Record<string, number> = {
  low: 1.0,
  medium: 1.25,
  high: 1.5,
};

// Grid sizes and target word counts by complexity
export const COMPLEXITY_GRID_SIZE: Record<string, number> = {
  low: 7,      // 7x7 beginner grid
  medium: 10,  // 10x10 intermediate grid
  high: 13,    // 13x13 expert grid (15x15 for standard mode)
};

export const COMPLEXITY_WORD_COUNT: Record<string, [number, number]> = {
  low: [8, 14],      // 8-14 words (7x7)
  medium: [16, 24],  // 16-24 words (10x10)
  high: [26, 34],    // 26-34 words (13x13)
};

export const COMPLEXITY_WORD_LENGTH: Record<string, [number, number]> = {
  low: [3, 6],     // 3-6 letters only
  medium: [3, 10], // 3-10 letters
  high: [3, 12],   // 3-12 letters
};

export const COMPLEXITY_MIN_SCORE: Record<string, number> = {
  low: 0,    // any score (short common words have low scores)
  medium: 40, // avoid obscure words
  high: 50,  // prioritize familiar words
};

export const COMPLEXITY_MAX_DESC_LENGTH: Record<string, number> = {
  low: 120,   // short, simple clues
  medium: 200, // moderate length
  high: 1000,  // full descriptions allowed
};

export const PREMIUM_COUNTS: Record<string, number> = {
  DL: 24,
  TL: 12,
  DW: 16,
  TW: 8,
};

export const PREMIUM_COLORS: Record<string, string> = {
  DL: "#93c5fd",
  TL: "#60a5fa",
  DW: "#f9a8d4",
  TW: "#f87171",
};

// Standard English letter tile distribution
export const LETTER_TILES: { letter: string; value: number; count: number }[] = [
  { letter: "A", value: 1, count: 9 },
  { letter: "B", value: 3, count: 2 },
  { letter: "C", value: 3, count: 2 },
  { letter: "D", value: 2, count: 4 },
  { letter: "E", value: 1, count: 12 },
  { letter: "F", value: 4, count: 2 },
  { letter: "G", value: 2, count: 3 },
  { letter: "H", value: 4, count: 2 },
  { letter: "I", value: 1, count: 9 },
  { letter: "J", value: 8, count: 1 },
  { letter: "K", value: 5, count: 1 },
  { letter: "L", value: 1, count: 4 },
  { letter: "M", value: 3, count: 2 },
  { letter: "N", value: 1, count: 6 },
  { letter: "O", value: 1, count: 8 },
  { letter: "P", value: 3, count: 2 },
  { letter: "Q", value: 10, count: 1 },
  { letter: "R", value: 1, count: 6 },
  { letter: "S", value: 1, count: 4 },
  { letter: "T", value: 1, count: 6 },
  { letter: "U", value: 1, count: 4 },
  { letter: "V", value: 4, count: 2 },
  { letter: "W", value: 4, count: 2 },
  { letter: "X", value: 8, count: 1 },
  { letter: "Y", value: 4, count: 2 },
  { letter: "Z", value: 10, count: 1 },
  { letter: "BLANK", value: 0, count: 2 },
];

export function createTileBag(tileCount?: number): { letter: string; value: number; count: number }[] {
  const bag: { letter: string; value: number; count: number }[] = [];
  for (const t of LETTER_TILES) {
    const count = tileCount
      ? Math.ceil((t.count / 100) * tileCount)
      : t.count;
    bag.push({ ...t, count: Math.max(1, count) });
  }
  return bag;
}

export const USERNAME_ADJECTIVES = [
  "Swift", "Brave", "Clever", "Mighty", "Silent", "Fierce", "Noble", "Wild",
  "Bright", "Dark", "Golden", "Iron", "Quick", "Sharp", "Bold", "Calm",
  "Cool", "Daring", "Eager", "Flash", "Gallant", "Happy", "Jolly", "Keen",
  "Lively", "Magic", "Nifty", "Proud", "Ready", "Sly", "Tough", "Vivid",
  "Wise", "Zesty", "Amber", "Azure", "Crimson", "Emerald", "Frost", "Shadow",
  "Storm", "Thunder", "Crystal", "Dusk", "Dawn", "Solar", "Lunar", "Nova",
  "Rocket", "Turbo", "Hyper", "Mega", "Super", "Ultra", "Prime", "Elite",
  "Ace", "Pro", "Star", "Spark", "Blaze", "Flare", "Glow", "Shine",
  "Buzz", "Zip", "Dash", "Zoom", "Rush", "Bolt", "Sprint", "Charge",
  "Sparkle", "Glimmer", "Shimmer", "Radiant", "Luminous", "Vibrant", "Dynamic", "Electric",
  "Fusion", "Quantum", "Nebula", "Comet", "Meteor", "Orbit", "Galaxy", "Cosmic",
  "Atomic", "Nuclear", "Plasma", "Fusion", "Vortex", "Matrix", "Cyber", "Digital",
  "Pixel", "Vector", "Binary", "Cipher", "Code", "Data", "Logic", "Sync",
  "Phase", "Pulse", "Wave", " Sonic", "Echo", "Chrono", "Tempo", "Rhythm",
  "Beat", "Flow", "Drift", "Glide", "Soar", "Rise", "Climb", "Leap",
  "Bound", "Spring", "Vault", "Summit", "Peak", "Crest", "Ridge", "Apex",
  "Zenith", "Crown", "Throne", "Realm", "Empire", "Kingdom", "Domain", "Nation",
  "Tribe", "Clan", "Guild", "Order", "League", "Union", "Alliance", "Pact",
  "Vow", "Oath", "Pledge", "Bond", "Link", "Tie", "Chain", "Ring",
  "Loop", "Knot", "Twist", "Spin", "Whirl", "Swirl", "Spiral", "Helix",
  "Curve", "Arc", "Bow", "Bend", "Flex", "Stretch", "Reach", "Span",
  "Bridge", "Cross", "Pass", "Way", "Path", "Trail", "Track", "Route",
  "Road", "Lane", "Line", "Course", "Race", "Chase", "Hunt", "Quest",
  "Mission", "Task", "Duty", "Honor", "Glory", "Fame", "Name", "Brand",
  "Mark", "Seal", "Sign", "Token", "Badge", "Crest", "Shield", "Guard",
  "Ward", "Keep", "Hold", "Grip", "Grasp", "Clutch", "Snatch", "Catch",
  "Grab", "Take", "Seize", "Claim", "Win", "Gain", "Earn", "Merit",
] as const;

export const USERNAME_NOUNS = [
  "Falcon", "Wolf", "Tiger", "Bear", "Eagle", "Hawk", "Lion", "Stallion",
  "Dragon", "Phoenix", "Raven", "Crow", "Owl", "Fox", "Lynx", "Panther",
  "Cobra", "Viper", "Shark", "Whale", "Dolphin", "Seal", "Otter", "Beaver",
  "Moose", "Elk", "Deer", "Buck", "Ram", "Bull", "Ox", "Boar",
  "Stag", "Hare", "Rabbit", "Squirrel", "Marten", "Badger", "Wolverine", "Coyote",
  "Jackal", "Hyena", "Leopard", "Cheetah", "Jaguar", "Puma", "Cougar", "Lynx",
  "Falcon", "Kestrel", "Merlin", "Osprey", "Kite", "Harrier", "Buzzard", "Vulture",
  "Condor", "Albatross", "Petrel", "Gull", "Tern", "Skua", "Frigate", "Booby",
  "Heron", "Egret", "Ibis", "Stork", "Crane", "Bustard", "Sandpiper", "Plover",
  "Dove", "Pigeon", "Parrot", "Macaw", "Cockatoo", "Lory", "Toucan", "Hornbill",
  "Kingfisher", "Bee-eater", "Roller", "Hoopoe", "Woodpecker", "Wryneck", "Piculet", "Barbet",
  "Trogon", "Quetzal", "Motmot", "Jacamar", "Puffbird", "Nunbird", "Monk", "Friar",
  "Cardinal", "Bishop", "Pope", "Abbe", "Prior", "Dean", "Canon", "Vicar",
  "Rector", "Pastor", "Parson", "Curate", "Clerk", "Scribe", "Writer", "Poet",
  "Bard", "Minstrel", "Troubadour", "Jester", "Fool", "Clown", "Mime", "Actor",
  "Player", "Gamer", "Pilot", "Driver", "Rider", "Racer", "Runner", "Jogger",
  "Sprinter", "Marathoner", "Walker", "Hiker", "Trekker", "Climber", "Scrambler", "Scaler",
  "Diver", "Swimmer", "Surfer", "Sailor", "Mariner", "Seaman", "Mate", "Crew",
  "Captain", "Skipper", "Commander", "Admiral", "General", "Colonel", "Major", "Captain",
  "Lieutenant", "Sergeant", "Corporal", "Private", "Trooper", "Ranger", "Scout", "Guide",
  "Leader", "Chief", "Boss", "Head", "Director", "Manager", "Agent", "Rep",
  "Broker", "Dealer", "Trader", "Merchant", "Vendor", "Seller", "Buyer", "Client",
  "Guest", "Visitor", "Traveler", "Tourist", "Pilgrim", "Wanderer", "Nomad", "Vagrant",
  "Rover", "Rover", "Rambler", "Rover", "Strider", "Stepper", "Pacer", "Marcher",
  "Drummer", "Fifer", "Bugler", "Trumpeter", "Trombonist", "Saxophonist", "Clarinetist", "Flutist",
  "Harpist", "Cellist", "Violinist", "Pianist", "Organist", "Conductor", "Composer", "Arranger",
  "Producer", "Engineer", "Designer", "Builder", "Maker", "Creator", "Founder", "Author",
  "Editor", "Publisher", "Printer", "Press", "Media", "News", "Report", "Story",
  "Tale", "Legend", "Myth", "Saga", "Epic", "Chronicle", "Record", "Archive",
  "Vault", "Safe", "Chest", "Box", "Case", "Kit", "Pack", "Bag",
  "Sack", "Pouch", "Purse", "Wallet", "Clip", "Pin", "Needle", "Thread",
  "Yarn", "Fiber", "Strand", "Chain", "Link", "Ring", "Band", "Loop",
  "Knot", "Tie", "Bow", "Ribbon", "Sash", "Sash", "Belt", "Strap",
  "Harness", "Yoke", "Collar", "Leash", "Rein", "Bridle", "Bit", "Spur",
  "Crop", "Whip", "Lash", "Switch", "Rod", "Staff", "Stick", "Pole",
  "Bar", "Rod", "Shaft", "Beam", "Truss", "Girder", "Joist", "Rafter",
  "Stud", "Post", "Pillar", "Column", "Pier", "Pile", "Stake", "Peg",
  "Pin", "Nail", "Spike", "Tack", "Brad", "Rivet", "Bolt", "Screw",
] as const;
